/**
 * 队内沟通接口（聊天 / 好友）。
 * 契约：
 *   GET    /conversations                        → {conversations,totalUnread}
 *   GET    /conversations/unread                 → {totalUnread}（导航角标）
 *   POST   /conversations/direct {userId}        → {conversation}（临时对话/好友私聊共用）
 *   GET    /conversations/:id/messages?afterSeq= → {conversationId,messages,nextAfterSeq}
 *   POST   /conversations/:id/messages {body}    → {message}
 *   POST   /conversations/:id/read               → {unread}
 *   GET    /friends                              → {friends,pending,incoming}
 *   POST   /friends/:userId                      → {friend}
 *   POST   /friends/:userId/accept|decline       → {friend}
 *   DELETE /friends/:userId                      → {removed}
 *   POST   /friends/:userId/block                → {blocked}
 *   DELETE /friends/:userId/block                → {unblocked}
 */
import { api, queryString } from './client.js';
import type {
  ChatMessageDto,
  ConversationDto,
  ConversationListDto,
  FriendDto,
  FriendListDto,
  MessageListDto,
} from './types.js';

export const chatApi = {
  listConversations: (): Promise<ConversationListDto> => api.get<ConversationListDto>('/conversations'),

  unreadTotal: (): Promise<{ totalUnread: number }> => api.get<{ totalUnread: number }>('/conversations/unread'),

  openDirect: (userId: string): Promise<{ conversation: ConversationDto }> =>
    api.post<{ conversation: ConversationDto }>('/conversations/direct', { userId }),

  /** 打开/创建队伍群聊会话（仅 accepted 成员） */
  openTeam: (teamId: string): Promise<{ conversation: ConversationDto }> =>
    api.post<{ conversation: ConversationDto }>(`/conversations/team/${encodeURIComponent(teamId)}`),

  listMessages: (conversationId: string, afterSeq = 0): Promise<MessageListDto> =>
    api.get<MessageListDto>(`/conversations/${encodeURIComponent(conversationId)}/messages${queryString({ afterSeq })}`),

  sendMessage: (conversationId: string, body: string): Promise<{ message: ChatMessageDto }> =>
    api.post<{ message: ChatMessageDto }>(`/conversations/${encodeURIComponent(conversationId)}/messages`, { body }),

  markRead: (conversationId: string): Promise<{ unread: number }> =>
    api.post<{ unread: number }>(`/conversations/${encodeURIComponent(conversationId)}/read`),

  // ---- 好友 ----
  listFriends: (): Promise<FriendListDto> => api.get<FriendListDto>('/friends'),

  requestFriend: (userId: string): Promise<{ friend: FriendDto }> =>
    api.post<{ friend: FriendDto }>(`/friends/${encodeURIComponent(userId)}`),

  acceptFriend: (userId: string): Promise<{ friend: FriendDto }> =>
    api.post<{ friend: FriendDto }>(`/friends/${encodeURIComponent(userId)}/accept`),

  declineFriend: (userId: string): Promise<{ friend: FriendDto }> =>
    api.post<{ friend: FriendDto }>(`/friends/${encodeURIComponent(userId)}/decline`),

  removeFriend: (userId: string): Promise<{ removed: boolean }> =>
    api.delete<{ removed: boolean }>(`/friends/${encodeURIComponent(userId)}`),

  blockUser: (userId: string): Promise<{ blocked: boolean }> =>
    api.post<{ blocked: boolean }>(`/friends/${encodeURIComponent(userId)}/block`),

  unblockUser: (userId: string): Promise<{ unblocked: boolean }> =>
    api.delete<{ unblocked: boolean }>(`/friends/${encodeURIComponent(userId)}/block`),
};
